import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";

import { InputLabel } from "../../../../components/inputs/InputLabel";
import { DelayReasonCode, LogDelayReasonPayload } from "../../../../types/onboarding";
import { DELAY_REASON_OPTIONS } from "../../../../constants/contractSigning";

interface LogDelayReasonDialogProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (payload: LogDelayReasonPayload) => Promise<boolean>;
}

/**
 * US4: record a categorical reason a contract is delayed.
 */
const LogDelayReasonDialog: React.FC<LogDelayReasonDialogProps> = ({
  visible,
  onHide,
  onSubmit,
}) => {
  const [reason, setReason] = useState<DelayReasonCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) {
      setReason(null);
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const submit = async () => {
    if (!reason) {
      setError("Please choose a reason.");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit({ reason_code: reason });
      if (ok) onHide();
    } catch (e) {
      setError("The delay reason could not be logged. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button label="Log delay reason" icon="pi pi-check" onClick={submit} loading={submitting} />
    </div>
  );

  return (
    <Dialog
      header="Log delay reason"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "32rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <Message
        severity="info"
        className="w-full mb-3"
        text="Nothing is sent to the customer. This records why the contract is taking longer, so the next person does not have to ask again."
      />
      <div className="field">
        <InputLabel name="delay_reason" label="Reason" required />
        <Dropdown
          inputId="delay_reason"
          value={reason}
          options={DELAY_REASON_OPTIONS}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => {
            setReason(e.value);
            setError(null);
          }}
          placeholder="Select a reason"
          className="w-full"
        />
      </div>
    </Dialog>
  );
};

export default LogDelayReasonDialog;
